// ── 지도 바텀시트 컴포넌트 ────────────────────────────────────────────────────
// - 마커 클릭 시 나타나는 슬라이드업 카드
// - 드래그 핸들 (non-passive touch 이벤트는 useMeetingMapCore에서 처리)
// - PC: × 버튼으로 닫기

import React from 'react';
import { CopyAddressButton } from '../../../components/meetingmap/CopyAddressButton';
import { PhoneValue } from '../../../components/meetingmap/PhoneValue';
import { PencilIcon } from '../../../components/meetingmap/Icons';
import { ticketLabel, TXT } from '../../../components/meetingmap/utils';
import {
  sheetStyle, sheetTopRowStyle, companyStyle, subTextStyle, ticketBadgeStyle,
  addressRowStyle, addressTextStyle,
  phoneInlineStyle, phoneSlashStyle, phoneParenStyle,
  contentRowStyle, contentLabelStyle, contentValueStyle,
  resultChipRowStyle, contractedChipStyle, cancelledChipStyle,
  keepChipStyle, feedbackChipStyle,
  resultLockedStyle, resultEntryBtnStyle,
} from '../../../components/meetingmap/styles';

export function MeetingSheet({
  item,             // 선택된 미팅 데이터
  isMobile,
  sheetDragY,       // 드래그 Y 오프셋(px)
  sheetDragging,    // useRef — 드래그 중 여부
  sheetHandleRef,   // 핸들 div ref (useMeetingMapCore 이펙트에서 사용)
  sheetRef,         // 시트 루트 div ref (useLeafletMap fitBounds 계산에 사용)
  SHEET_DISMISS_THRESHOLD,
  onClose,          // 닫기 콜백
  onResultOpen,     // 결과 입력 모달 열기 콜백
  showToast,
}) {
  return (
    <div
      ref={sheetRef}
      style={{
        ...sheetStyle(isMobile),
        transform: `translateY(${sheetDragY}px)`,
        transition: sheetDragging.current ? 'none' : 'transform 0.32s cubic-bezier(0.34,1.56,0.64,1)',
        opacity: 1 - Math.min(sheetDragY / SHEET_DISMISS_THRESHOLD, 1) * 0.25,
      }}
    >
      {/* ── 핸들 + PC 닫기(×) 버튼 래퍼 ─────────────────────────── */}
      <div style={{ position: 'relative', width: '100%' }}>
        {/* 핸들 — 이 영역만 드래그 감지 */}
        <div
          ref={sheetHandleRef}
          style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            padding: '18px 0 16px', userSelect: 'none', cursor: 'grab', width: '100%',
          }}
        >
          <div style={{
            width: sheetDragY > 30 ? 52 : 40, height: 5, borderRadius: 999,
            background: sheetDragY > 50 ? 'var(--text-muted)' : 'var(--border)',
            transition: 'width 0.15s, background 0.15s',
          }} />
          <div style={{
            fontSize: 10, fontWeight: 700, marginTop: 4, letterSpacing: '0.04em',
            color: sheetDragY > SHEET_DISMISS_THRESHOLD * 0.6 ? 'var(--mm-tone-blue-text)' : 'transparent',
            transition: 'color 0.15s',
          }}>놓으면 닫힘</div>
        </div>
        {/* PC용 × 닫기 버튼 — 우상단 고정 */}
        <button
          type="button"
          onClick={onClose}
          style={{
            position: 'absolute', top: 10, right: 12,
            width: 28, height: 28, borderRadius: '50%',
            border: 'none', background: 'var(--bg-subtle)', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 16, color: 'var(--text-muted)', lineHeight: 1,
            transition: 'background 0.15s, color 0.15s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--border)'; e.currentTarget.style.color = 'var(--text)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--bg-subtle)'; e.currentTarget.style.color = 'var(--text-muted)'; }}
          aria-label="닫기"
        >×</button>
      </div>

      {/* ── 상호명 + 상품 뱃지 ──────────────────────────────────── */}
      <div style={sheetTopRowStyle}>
        <div style={{ minWidth: 0 }}>
          <div style={companyStyle}>{item.company_name || '-'}</div>
          <div style={subTextStyle}>
            {item.ceo_name || '-'} &middot; {item.visit_time || '-'}
          </div>
        </div>
        <span style={ticketBadgeStyle}>{ticketLabel(item.contract_items, item.ticket_type)}</span>
      </div>

      {/* ── 주소 ────────────────────────────────────────────────── */}
      <div style={addressRowStyle}>
        <div style={addressTextStyle}>{item.address || TXT.noAddress}</div>
        <CopyAddressButton
          address={item.address}
          onError={() => showToast('복사에 실패했습니다.', 'error')}
        />
      </div>

      {/* ── 전화번호 ─────────────────────────────────────────────── */}
      <div style={phoneInlineStyle}>
        <span style={phoneParenStyle}>(</span>
        <PhoneValue value={item.phone} />
        <span style={phoneSlashStyle}> / </span>
        <PhoneValue value={item.mobile_phone} />
        <span style={phoneParenStyle}>)</span>
      </div>

      {/* ── 내용 ────────────────────────────────────────────────── */}
      <div style={contentRowStyle}>
        <span style={contentLabelStyle}>{TXT.content}</span>
        <div style={contentValueStyle}>{String(item.content || '').trim() || '-'}</div>
      </div>

      {/* ── 저장된 피드백 / 계약 결과 chip ────────────────────────── */}
      {(item.is_contracted || item.is_keep || item.feedback) && (
        <div style={resultChipRowStyle}>
          {item.is_contracted && (
            item.contract_status === 'cancelled'
              ? <span style={cancelledChipStyle}>계약취소</span>
              : <span style={contractedChipStyle}>✓ 계약완료</span>
          )}
          {item.is_keep && <span style={keepChipStyle}>☆ 킵</span>}
          {/* 피드백 chip: 킵 여부와 무관하게 피드백이 있으면 표시 */}
          {item.feedback && !item.is_contracted && (
            <span style={feedbackChipStyle(item.feedback_color_key)}>{item.feedback}</span>
          )}
        </div>
      )}

      {/* ── 결과 입력/수정 버튼 — 계약완료/취소 후에는 잠금 ─────── */}
      {item.is_contracted ? (
        <div style={resultLockedStyle}>
          {item.contract_status === 'cancelled'
            ? '계약취소 (수정 불가)'
            : '✓ 계약 접수 완료 (수정 불가)'}
        </div>
      ) : (
        <button
          type="button"
          style={resultEntryBtnStyle}
          onClick={() => onResultOpen(item)}
        >
          <PencilIcon size={13} />
          {item.feedback ? TXT.resultEdit : TXT.resultInput}
        </button>
      )}
    </div>
  );
}
